import { formatDate } from '../../../../../utils/date';
import { convertAmount, convertCurrency } from '../../../../../utils/amount';

const types = {
  LESS_THAN: 'lower than',
  GREATER_THAN: 'higher than',
  EQUALS: 'equal to',
  CONTAINS: 'that contains',
  BETWEEN: 'between',
  NOT_CONTAINS: 'that not contains',
  STARTS_WITH: 'that starts with',
  ENDS_WITH: 'that ends with',
};

const getTexts = ({ type }) => ({
  ACCOUNT_STATUS: 'The account <strong>{{accountNumber}}</strong> has been blocked by the Institution.',
  ACCOUNT_BALANCE: `The account <strong>{{accountNumber}}</strong> has a balance ${type} {{constraintValue}}.`,
  ACCOUNT_BLOCKED_JUDICIAL: `The account <strong>{{accountNumber}}</strong> has a judicial blocked balance ${type} {{constraintValue}}.`,
  ACCOUNT_BALANCE_BETWEEN: `The account <strong>{{accountNumber}}</strong> has a balance ${type} {{constraintValue.constraintValueFrom}} and {{constraintValue.constraintValueTo}}.`,
  TRANSACTION_AMOUNT: `The account <strong>{{accountNumber}}</strong> registered a transaction with an amount ${type} {{constraintValue}}.`,
  TRANSACTION_AMOUNT_BETWEEN: `The account <strong>{{accountNumber}}</strong> registered a transaction with an amount ${type} {{constraintValue.constraintValueFrom}} and {{constraintValue.constraintValueTo}}.`,
  TRANSACTION_REFERENCE: `The account <strong>{{accountNumber}}</strong> registered a transaction with a reference ${type} {{constraintValue}}.`,
  TRANSACTION_EXTERNAL_ID: `The account <strong>{{accountNumber}}</strong> registered a transaction with an external ID ${type} {{constraintValue}}.`,
  TRANSACTION_DESCRIPTION: `The account <strong>{{accountNumber}}</strong> registered a transaction with a description ${type} {{constraintValue}}.`,
  TRANSACTION_CATEGORY: `The account <strong>{{accountNumber}}</strong> registered a transaction with a category ${type} {{constraintValue}}.`,
  TRANSACTION_SENDER: `The account <strong>{{accountNumber}}</strong> registered a transaction with a sender ${type} {{constraintValue}}.`,
  TRANSACTION_SENDER_DOCUMENT: `The account <strong>{{accountNumber}}</strong> registered a transaction with a sender's document ${type} {{constraintValue}}.`,
  TRANSACTION_BENEFICIARY: `The account <strong>{{accountNumber}}</strong> registered a transaction with a beneficiary ${type} {{constraintValue}}.`,
  TRANSACTION_BENEFICIARY_DOCUMENT: `The account <strong>{{accountNumber}}</strong> registered a transaction with a beneficiary's document ${type} {{constraintValue}}.`,
  TRANSACTION_FEE: `The account <strong>{{accountNumber}}</strong> registered a transaction with a fee ${type} {{constraintValue}}.`,
  TRANSACTION_FEE_BETWEEN: `The account <strong>{{accountNumber}}</strong> registered a transaction with a fee ${type} {{constraintValue.constraintValueFrom}} and {{constraintValue.constraintValueTo}}.`,
  INVESTMENT_APPLICATION_DUE_DATE_BOTH: 'You have a total of {{currency}} {{todayAmount}} in investments that expires today. You have a total of {{currency}} {{tomorrowAmount}} in investments that expires tomorrow.',
  INVESTMENT_APPLICATION_DUE_DATE_TODAY: 'You have a total of {{currency}} {{todayAmount}} in investments that expires today.',
  INVESTMENT_APPLICATION_DUE_DATE_TOMORROW: 'You have a total of {{currency}} {{tomorrowAmount}} in investments that expires tomorrow.',
  PAYMENT_AMOUNT: `The account <strong>{{accountNumber}}</strong> registered a payment with an amount ${type} {{constraintValue}}.`,
  PAYMENT_AMOUNT_BETWEEN: `The account <strong>{{accountNumber}}</strong> registered a payment with an amount ${type} {{constraintValue.constraintValueFrom}} and {{constraintValue.constraintValueTo}}.`,
  PAYMENT_REFERENCE: `The account <strong>{{accountNumber}}</strong> registered a payment with a reference ${type} {{constraintValue}}.`,
  PAYMENT_DESCRIPTION: `The account <strong>{{accountNumber}}</strong> registered a payment with a description ${type} {{constraintValue}}.`,
  PAYMENT_CATEGORY: `The account <strong>{{accountNumber}}</strong> registered a payment with a category ${type} {{constraintValue}}.`,
  PAYMENT_BENEFICIARY: `The account <strong>{{accountNumber}}</strong> registered a payment with a beneficiary ${type} {{constraintValue}}.`,
  PAYMENT_BENEFICIARY_DOCUMENT: `The account <strong>{{accountNumber}}</strong> registered a payment with a beneficiary's document ${type} {{constraintValue}}.`,
});

export const convertNotificationText = ({
  constraintType,
  constraintField,
  constraintResource,
  accountNumber,
  constraintValue,
  currency,
  todayAmount,
  tomorrowAmount,
}) => {
  let constraintValueParsed = {};

  if (constraintType === 'BETWEEN') {
    constraintValueParsed = JSON.parse(constraintValue);
  }

  const isValidBetween = constraintType === 'BETWEEN' && ['AMOUNT', 'BALANCE'].includes(constraintField);
  let textKey = isValidBetween ? `${constraintResource}_${constraintField}_BETWEEN` : `${constraintResource}_${constraintField}`;
  const type = types[constraintType] || '';
  const texts = getTexts({ type });

  const constraintValues = {
    BALANCE: `${convertCurrency(currency)} ${convertAmount(currency, constraintValue, 'decimal')}`,
    BALANCE_FROM: `${convertCurrency(currency)} ${convertAmount(currency, constraintValueParsed.constraintValueFrom, 'decimal')}`,
    BALANCE_TO: `${convertCurrency(currency)} ${convertAmount(currency, constraintValueParsed.constraintValueTo, 'decimal')}`,
    AMOUNT: `${convertCurrency(currency)} ${convertAmount(currency, constraintValue, 'decimal')}`,
    AMOUNT_FROM: `${convertCurrency(currency)} ${convertAmount(currency, constraintValueParsed.constraintValueFrom, 'decimal')}`,
    AMOUNT_TO: `${convertCurrency(currency)} ${convertAmount(currency, constraintValueParsed.constraintValueTo, 'decimal')}`,
    FEE_FROM: `${convertCurrency(currency)} ${convertAmount(currency, constraintValueParsed.constraintValueFrom, 'decimal')}`,
    FEE_TO: `${convertCurrency(currency)} ${convertAmount(currency, constraintValueParsed.constraintValueTo, 'decimal')}`,
  };

  const newConstraintValue = isValidBetween
    ? ({
      constraintValueFrom: constraintValues[`${constraintField}_FROM`] || constraintValueParsed.constraintValueFrom,
      constraintValueTo: constraintValues[`${constraintField}_TO`] || constraintValueParsed.constraintValueTo,
    }) : (
      constraintValues[constraintField] || constraintValue
    );

  if (textKey === 'INVESTMENT_APPLICATION_DUE_DATE') {
    textKey = 'INVESTMENT_APPLICATION_DUE_DATE_BOTH';
    if (todayAmount === 0) textKey = 'INVESTMENT_APPLICATION_DUE_DATE_TOMORROW';
    if (tomorrowAmount === 0) textKey = 'INVESTMENT_APPLICATION_DUE_DATE_TODAY';
  }

  return {
    text: texts[textKey] || '',
    textOptions: {
      accountNumber,
      currency: convertCurrency(currency),
      todayAmount: convertAmount(currency, todayAmount),
      tomorrowAmount: convertAmount(currency, tomorrowAmount),
      constraintValue: newConstraintValue,
      dueDate: formatDate(constraintValue, 'L'),
    },
  };
};
